import { PERMISSIONS, type Permission } from './permissions'

export const MODULE_LABELS: Record<keyof typeof PERMISSIONS, string> = {
  company: 'Empresas',
  driver: 'Motoristas',
  vehicle: 'Veículos',
  freight: 'Fretes',
  invoice: 'Faturas',
  repayment: 'Repasses',
  cash: 'Fluxo de Caixa',
  obligation: 'Contas a Pagar/Receber',
  expense: 'Despesas Operacionais',
  employee: 'Funcionários',
  reports: 'Relatórios',
  team: 'Equipe',
  settings: 'Configurações',
}

interface PermissionMeta {
  label: string
  description?: string
}

export const PERMISSION_META: Record<Permission, PermissionMeta> = {
  // Empresas
  'company.read': { label: 'Visualizar empresas', description: 'Listar e ver detalhes de empresas clientes.' },
  'company.create': { label: 'Cadastrar empresas' },
  'company.update': { label: 'Editar empresas' },
  'company.deactivate': { label: 'Ativar/desativar empresas' },

  // Motoristas
  'driver.read': { label: 'Visualizar motoristas' },
  'driver.create': { label: 'Cadastrar motoristas' },
  'driver.update': { label: 'Editar motoristas' },
  'driver.deactivate': { label: 'Ativar/desativar motoristas' },

  // Veículos
  'vehicle.read': { label: 'Visualizar veículos' },
  'vehicle.create': { label: 'Cadastrar veículos' },
  'vehicle.update': { label: 'Editar veículos' },
  'vehicle.deactivate': { label: 'Ativar/desativar veículos' },
  'vehicle.view_financial_summary': {
    label: 'Ver resumo financeiro do veículo',
    description: 'Receita, custo de motoristas e despesas por veículo no período.',
  },

  // Fretes
  'freight.read': { label: 'Visualizar fretes' },
  'freight.create': { label: 'Cadastrar fretes' },
  'freight.update': { label: 'Editar fretes' },
  'freight.assign': { label: 'Atribuir motorista ao frete' },
  'freight.set_value': {
    label: 'Definir/editar valor do frete',
    description: 'Necessário para informar o valor cobrado da empresa contratante.',
  },
  'freight.complete': { label: 'Avançar estados do frete (iniciar/entregar/completar)' },

  // Faturas
  'invoice.read': { label: 'Visualizar faturas' },
  'invoice.create': { label: 'Cadastrar faturas' },
  'invoice.update': { label: 'Editar faturas' },
  'invoice.pay': { label: 'Registrar pagamento de fatura' },
  'invoice.delete': { label: 'Excluir faturas' },

  // Repasses
  'repayment.read': { label: 'Visualizar repasses' },
  'repayment.update': { label: 'Editar repasse (ajustar valor)' },
  'repayment.pay': { label: 'Registrar pagamento de repasse' },

  // Caixa
  'cash.read': { label: 'Visualizar fluxo de caixa' },
  'cash.write': {
    label: 'Lançar movimentações no caixa',
    description: 'Criar entradas, saídas e transferências manuais.',
  },
  'cash.reverse': { label: 'Estornar lançamentos manuais' },
  'cash.manage_accounts': { label: 'Gerenciar contas de caixa' },
  'cash.manage_categories': { label: 'Gerenciar categorias de caixa' },
  'cash.manage_opening_balance': {
    label: 'Editar saldo inicial das contas',
    description: 'Operação sensível: muda a base do saldo histórico.',
  },

  // Obrigações
  'obligation.read': { label: 'Visualizar contas a pagar/receber' },
  'obligation.write': { label: 'Cadastrar e editar contas a pagar/receber' },
  'obligation.settle': { label: 'Quitar contas' },

  // Despesas operacionais
  'expense.read': { label: 'Visualizar despesas operacionais' },
  'expense.create': { label: 'Cadastrar despesas operacionais' },

  // Funcionários
  'employee.read': { label: 'Visualizar funcionários' },
  'employee.write': { label: 'Cadastrar e editar funcionários' },
  'employee.pay_salary': { label: 'Registrar pagamento de salário' },

  // Relatórios
  'reports.view': {
    label: 'Acessar relatórios',
    description: 'DRE, rentabilidade de fretes/motoristas/veículos.',
  },

  // Equipe
  'team.read': { label: 'Listar membros da equipe' },
  'team.invite': { label: 'Enviar convites' },
  'team.manage_users': {
    label: 'Gerenciar usuários',
    description: 'Mudar função, ativar/desativar, gerar reset de senha.',
  },
  'team.manage_roles': {
    label: 'Gerenciar funções e permissões',
    description: 'Acesso a esta tela. Cuidado ao remover: o tenant precisa de pelo menos uma função com esta permissão.',
  },

  // Configurações
  'settings.appearance': { label: 'Mudar cor primária da marca' },
  'settings.view_audit': { label: 'Visualizar log de atividades' },
}

export interface ModuleGroup {
  module: keyof typeof PERMISSIONS
  label: string
  permissions: ReadonlyArray<{ key: Permission; label: string; description?: string }>
}

export const MODULE_GROUPS: ReadonlyArray<ModuleGroup> = (Object.entries(PERMISSIONS) as Array<[
  keyof typeof PERMISSIONS,
  ReadonlyArray<string>,
]>).map(([mod, actions]) => ({
  module: mod,
  label: MODULE_LABELS[mod],
  permissions: actions.map((a) => {
    const key = `${mod}.${a}` as Permission
    return { key, label: PERMISSION_META[key].label, description: PERMISSION_META[key].description }
  }),
}))
